import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { listFiles, deleteFile, humanBytes, fileBlobUrl } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, FileText, ExternalLink, Download, Image as ImageIcon, Film, Music, Table as TableIcon, Presentation, Archive, File as FileIcon, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import FileDropZone from "@/components/FileDropZone";
import FilePreviewDialog from "@/components/FilePreviewDialog";

const TYPES = ["contracts","invoices","receipts","pitch decks","scripts","proposals","agreements","reports","marketing assets","insurance","general files"];

const MIME_ICON = (mime = "") => {
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.startsWith("video/")) return Film;
  if (mime.startsWith("audio/")) return Music;
  if (mime === "application/pdf" || mime.includes("word")) return FileText;
  if (mime === "text/csv" || mime.includes("sheet")) return TableIcon;
  if (mime.includes("presentation")) return Presentation;
  if (mime.includes("zip") || mime.includes("compressed")) return Archive;
  return FileIcon;
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [files, setFiles] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");
  const [folderFilter, setFolderFilter] = useState("all");
  const [uploadProject, setUploadProject] = useState("none");
  const [uploadFolder, setUploadFolder] = useState("general files");
  const [linking, setLinking] = useState(null); // { kind: "file" | "doc", item }
  const [preview, setPreview] = useState(null); // {file, url}

  const load = async () => {
    const query = {};
    if (projectFilter !== "all") query.project_id = projectFilter;
    if (folderFilter !== "all") query.folder_key = folderFilter;
    const [d, p, f] = await Promise.all([
      api.get("/documents"),
      api.get("/projects"),
      listFiles(query),
    ]);
    setDocs(d.data);
    setProjects(p.data);
    setFiles(f);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectFilter, folderFilter]);

  const saveDoc = async (form) => {
    try { await api.post("/documents", form); toast.success("Saved"); setOpen(false); load(); }
    catch { toast.error("Failed"); }
  };
  const removeDoc = async (id) => { if (!window.confirm("Delete document?")) return; await api.delete(`/documents/${id}`); load(); };

  const onUploaded = (record) => setFiles((prev) => [record, ...prev]);
  const removeFile = async (id) => {
    if (!window.confirm("Remove this file? It'll be soft-deleted and hidden from your library.")) return;
    try { await deleteFile(id); setFiles((prev) => prev.filter((f) => f.id !== id)); toast.success("File removed"); }
    catch { toast.error("Failed to remove file"); }
  };

  const saveLink = async ({ project_id, folder_key }) => {
    const { kind, item } = linking;
    try {
      if (kind === "file") {
        const { data } = await api.patch(`/files/${item.id}`, { project_id, folder_key });
        setFiles((prev) => prev.map((f) => (f.id === item.id ? data : f)));
      } else {
        await api.put(`/documents/${item.id}`, { project_id: project_id || null, doc_type: folder_key || item.doc_type });
        await load();
      }
      toast.success("Linked");
      setLinking(null);
    } catch { toast.error("Could not link that"); }
  };

  const uploadContext = {
    ...(uploadProject !== "none" ? { project_id: uploadProject } : {}),
    ...(uploadFolder !== "none" ? { folder_key: uploadFolder } : {}),
  };
  const projectTitle = (id) => projects.find((p) => p.id === id)?.title;

  const openPreview = (file) => setPreview({ file });
  const closePreview = () => setPreview(null);
  const downloadFile = async (file) => {
    try {
      const url = await fileBlobUrl(file.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Documents & files</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload real files or link out to hosted documents. Everything is scoped to your account.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-44" data-testid="docs-project-filter">
              <SelectValue placeholder="All projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={folderFilter} onValueChange={setFolderFilter}>
            <SelectTrigger className="w-40" data-testid="docs-folder-filter">
              <SelectValue placeholder="All folders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button variant="outline" data-testid="add-document-btn"><Plus className="w-4 h-4 mr-2"/> Link document</Button></DialogTrigger>
            <DocDialog onSave={saveDoc} projects={projects}/>
          </Dialog>
        </div>
      </div>

      <Card className="studio-card p-4" data-testid="upload-destination">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="space-y-1.5 sm:w-60">
            <Label className="text-xs">Upload into project</Label>
            <Select value={uploadProject} onValueChange={setUploadProject}>
              <SelectTrigger data-testid="upload-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:w-56">
            <Label className="text-xs">Folder</Label>
            <Select value={uploadFolder} onValueChange={setUploadFolder}>
              <SelectTrigger data-testid="upload-folder-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No folder</SelectItem>
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground sm:pb-2">
            Anything you drop below lands here — you can re-file it later with <strong>Link</strong>.
          </p>
        </div>
      </Card>

      <FileDropZone
        context={uploadContext}
        onUploaded={onUploaded}
        label="Drop files here to upload — images, PDFs, video, docs"
        data-testid="documents-dropzone"
      />

      <Tabs defaultValue="files">
        <TabsList>
          <TabsTrigger value="files" data-testid="tab-files">Uploaded files ({files.length})</TabsTrigger>
          <TabsTrigger value="links" data-testid="tab-links">Linked documents ({docs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="mt-4">
          {files.length === 0 ? (
            <Card className="studio-card p-10 text-center" data-testid="files-empty">
              <FileText className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5}/>
              <div className="text-lg font-medium">No files yet</div>
              <p className="text-sm text-muted-foreground mt-1">Drag files into the box above to get started.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {files.map((f) => {
                const Icon = MIME_ICON(f.content_type);
                const isImage = (f.content_type || "").startsWith("image/");
                return (
                  <Card key={f.id} className="studio-card p-4 group" data-testid={`file-card-${f.id}`}>
                    <div className="flex items-start gap-3">
                      {isImage ? (
                        <ImageThumb file={f} onClick={() => openPreview(f)} />
                      ) : (
                        <button
                          onClick={() => openPreview(f)}
                          className="h-14 w-14 shrink-0 rounded-lg bg-accent/60 flex items-center justify-center hover:bg-accent transition-colors"
                        >
                          <Icon className="w-6 h-6 text-muted-foreground" />
                        </button>
                      )}
                      <div className="flex-1 min-w-0">
                        <button
                          onClick={() => openPreview(f)}
                          className="text-left font-medium text-sm truncate w-full hover:text-[hsl(var(--studio-primary))]"
                          title={f.original_filename}
                        >
                          {f.original_filename}
                        </button>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                          <span>{humanBytes(f.size)}</span>
                          <span>·</span>
                          <span>{(f.content_type || "").split("/")[1] || "file"}</span>
                          {f.project_id && (
                            <Badge variant="secondary" className="text-[10px] font-normal"
                                   data-testid={`file-project-badge-${f.id}`}>
                              {projectTitle(f.project_id) || "project"}
                            </Badge>
                          )}
                          {f.folder_key && (
                            <Badge variant="outline" className="text-[10px] font-normal"
                                   data-testid={`file-folder-badge-${f.id}`}>
                              {f.folder_key}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-between">
                      <div className="text-[11px] text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</div>
                      <div className="flex gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="sm" data-testid={`file-link-${f.id}`}
                                onClick={() => setLinking({ kind: "file", item: f })}>
                          <LinkIcon className="w-3.5 h-3.5 mr-1"/> Link
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadFile(f)} data-testid={`file-download-${f.id}`}><Download className="w-3.5 h-3.5"/></Button>
                        <Button variant="ghost" size="sm" onClick={() => removeFile(f.id)} data-testid={`file-delete-${f.id}`}><Trash2 className="w-3.5 h-3.5"/></Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="links" className="mt-4">
          {docs.length === 0 ? (
            <Card className="studio-card p-10 text-center">
              <FileText className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" strokeWidth={1.5}/>
              <div className="text-lg font-medium">No linked documents yet</div>
              <p className="text-sm text-muted-foreground mt-1">Add a link to a hosted doc (Google Docs, Notion, etc.).</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {docs.map((d) => (
                <Card key={d.id} className="studio-card p-5" data-testid={`document-card-${d.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-accent/60 flex items-center justify-center"><FileText className="w-5 h-5 text-muted-foreground"/></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{d.title}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <Badge variant="secondary" className="text-[10px] font-normal">{d.doc_type}</Badge>
                        {d.project_id && (
                          <Badge variant="outline" className="text-[10px] font-normal"
                                 data-testid={`doc-project-badge-${d.id}`}>
                            {projectTitle(d.project_id) || "project"}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  {d.notes && <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{d.notes}</p>}
                  <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" data-testid={`doc-link-${d.id}`}
                              onClick={() => setLinking({ kind: "doc", item: d })}>
                        <LinkIcon className="w-3.5 h-3.5 mr-1"/> Link
                      </Button>
                      {d.url && <a href={d.url} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm"><ExternalLink className="w-3.5 h-3.5"/></Button></a>}
                      <Button variant="ghost" size="sm" onClick={() => removeDoc(d.id)}><Trash2 className="w-3.5 h-3.5"/></Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <LinkDialog target={linking} projects={projects} onClose={() => setLinking(null)} onSave={saveLink} />

      {/* File preview */}
      <FilePreviewDialog file={preview?.file} open={!!preview} onClose={closePreview} />
    </div>
  );
}

function LinkDialog({ target, projects, onClose, onSave }) {
  const item = target?.item;
  const [projectId, setProjectId] = useState("none");
  const [folder, setFolder] = useState("none");

  useEffect(() => {
    if (!item) return;
    setProjectId(item.project_id || "none");
    setFolder(item.folder_key || item.doc_type || "none");
  }, [item]);

  if (!target) return null;
  const isFile = target.kind === "file";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="link-file-dialog">
        <DialogHeader>
          <DialogTitle>Link “{item.original_filename || item.title}”</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label className="text-xs">Project</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="link-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Folder</Label>
            <Select value={folder} onValueChange={setFolder}>
              <SelectTrigger data-testid="link-folder-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isFile && <SelectItem value="none">No folder</SelectItem>}
                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="link-cancel">Cancel</Button>
          <Button data-testid="link-save"
                  onClick={() => onSave({
                    project_id: projectId === "none" ? null : projectId,
                    folder_key: folder === "none" ? null : folder,
                  })}>
            Save link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImageThumb({ file, onClick }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    let created = null;
    fileBlobUrl(file.id).then((u) => {
      if (!active) { URL.revokeObjectURL(u); return; }
      created = u;
      setUrl(u);
    }).catch((err) => console.error("Could not load documents", err));
    return () => { active = false; if (created) URL.revokeObjectURL(created); };
  }, [file.id]);
  return (
    <button onClick={onClick} className="h-14 w-14 shrink-0 rounded-lg bg-accent/60 flex items-center justify-center overflow-hidden">
      {url ? (
        <img src={url} alt={file.original_filename} className="w-full h-full object-cover" />
      ) : (
        <ImageIcon className="w-5 h-5 text-muted-foreground" />
      )}
    </button>
  );
}

function DocDialog({ onSave, projects }) {
  const [f, setF] = useState({ title: "", doc_type: "general files", url: "", notes: "", project_id: "" });
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>Link an external document</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="doc-title" value={f.title} onChange={(e) => setF({...f, title: e.target.value})}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={f.doc_type} onValueChange={(v) => setF({...f, doc_type: v})}>
              <SelectTrigger data-testid="doc-type"><SelectValue/></SelectTrigger>
              <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Linked project</Label>
            <Select value={f.project_id || "__none__"} onValueChange={(v) => setF({...f, project_id: v === "__none__" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">URL (link to file)</Label><Input value={f.url} onChange={(e) => setF({...f, url: e.target.value})}/></div>
        <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})}/></div>
      </div>
      <DialogFooter><Button data-testid="doc-save" onClick={() => onSave(f)}>Save</Button></DialogFooter>
    </DialogContent>
  );
}
